import { TreeViewBaseItem } from "@mui/x-tree-view";
import { uuid } from "@sinch/utils/crypto";
import { get } from "@sinch/utils/path/get";
import { set } from "@sinch/utils/path/set";
import { unset } from "@sinch/utils/path/unset";
import { difference, without } from "ramda";
import isNil from "ramda/es/isNil";
import { ensureArray, isNotNil } from "ramda-adjunct";
import { Dispatch, useCallback, useEffect, useReducer } from "react";
import { FieldValues } from "react-hook-form";
import {
  AddAction,
  CopyAction,
  ExpandAction,
  History,
  MoveAction,
  RateBuilderControls,
  RateMethods,
  RateTreeActions,
  RateTreeActionTypes,
  RateTreeReducer,
  RateTreeState,
  RemoveAction,
  TreeMethods,
  UseRateBuilderProps,
  UseRateBuilderPropsReturn,
} from "@sinch/components/application/RatelistBuilder/types";
import { extractNodeIds } from "@sinch/components/application/RatelistBuilder/utils/extractNodeIds";
import { back, forward, snapshot } from "@sinch/components/application/RatelistBuilder/utils/historyUtils";
import {
  collectAncestorIds,
  copyNodeInTree,
  getIndexOfNodeById,
  getNode,
  getParentNodeById,
  getTreeIds,
  getTreeLeafsIds,
  insertIntoTree,
  moveNodeInTree,
  removeFromTree,
} from "@sinch/components/application/RatelistBuilder/utils/treeUtils";

export const useRateBuilder = <TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues>({
  initialData,
  dataPathGetter,
  newIdGen: newId = () => uuid(),
  options,
}: UseRateBuilderProps<TreeItem, TData>): UseRateBuilderPropsReturn<TreeItem, TData> => {
  type TM = TreeMethods<TreeItem, TData>;
  /**
   * Adds a new node and its associated data to the tree state.
   *
   * @param currentState - The current state of the tree.
   * @param action - The action containing the data of the node to add.
   * @returns - The new state of the tree, or null if the node could not be added.
   */
  const add = useCallback(
    (currentState: RateTreeState<TreeItem, TData>, action: AddAction<TreeItem, TData>) => {
      // Get new Id
      const newParentId = action.treeItem?.id ?? newId();
      // Get ids from new data tree
      const ids = action.treeItem ? extractNodeIds(ensureArray(action.treeItem)) : [newId()];
      // Set new data to store
      for (const id of ids) {
        if (action.data) {
          set(currentState.data, dataPathGetter(id), action.data);
        }
      }
      // Insert new data into tree
      const newTree = insertIntoTree<TreeItem>(currentState.tree, action.parentId, {
        id: newParentId,
        label: newParentId,
        ...action.treeItem,
      } as TreeItem);

      if (!newTree) {
        return null;
      }

      const expanded = [...without([action.parentId], currentState.expanded), action.parentId].filter((element) =>
        isNotNil(element)
      );

      return {
        ...currentState,
        expanded: expanded,
        tree: newTree,
        data: { ...currentState.data },
      };
    },
    [dataPathGetter, newId]
  );

  /**
   * Removes a node and its associated data from the tree state.
   *
   * @param currentState - The current state of the tree.
   * @param action - The action containing the ID of the node to remove.
   * @returns - The new state of the tree, or null if the node to delete is not found.
   */
  const remove = useCallback(
    (currentState: RateTreeState<TreeItem, TData>, action: RemoveAction) => {
      const parent = getParentNodeById(currentState.tree, action.nodeId);
      // Get the node to delete from the tree
      const nodeToDelete = getNode(currentState.tree, action.nodeId);
      if (isNil(nodeToDelete)) {
        return null;
      }

      // Get the IDs of the node and its children
      const idsToDelete = getTreeIds(ensureArray(nodeToDelete));

      // Remove the data associated with each ID
      for (const id of idsToDelete) {
        unset(currentState.data, dataPathGetter(id));
      }

      // Remove the node from the tree
      const newTree = removeFromTree<TreeItem>(currentState.tree, action.nodeId);
      if (isNil(newTree)) {
        return null;
      }

      const isParentEmptyAfterRemove = parent && (getNode(newTree, parent.id)?.children?.length ?? 0) === 0;

      // Return the new state of the tree
      return {
        ...currentState,
        expanded: isParentEmptyAfterRemove ? without([parent.id], currentState.expanded) : currentState.expanded,
        tree: newTree,
        data: { ...currentState.data },
      };
    },
    [dataPathGetter]
  );

  /**
   * Copies a node and its associated data within the tree state.
   * This function:
   * 1. Creates new unique IDs for the copied node and all its children
   * 2. Maintains a mapping between original and new IDs
   * 3. Copies all associated data from original nodes to their copies
   *
   * @param currentState - The current state of the tree
   * @param action - The action containing the ID of the node to copy
   * @returns The new state of the tree with the copied nodes, or null if the node to copy is not found
   */
  const copy = useCallback(
    (currentState: RateTreeState<TreeItem, TData>, action: CopyAction) => {
      // Find the node to copy
      const nodeToCopy = getNode(currentState.tree, action.nodeId);
      if (isNil(nodeToCopy)) {
        return null;
      }

      // Track old to new ID mappings
      const idMap: { oldId: string; newId: string }[] = [];

      // Copy the node in the tree with new IDs
      const newTree = copyNodeInTree(currentState.tree, action.nodeId, (oldId) => {
        const newItemId = newId();
        idMap.push({ oldId, newId: newItemId });
        return newItemId;
      });
      if (isNil(newTree)) {
        return null;
      }
      // Copy associated data for each mapped ID
      for (const { oldId, newId: newItemId } of idMap) {
        const oldDataPath = dataPathGetter(oldId);
        const newDataPath = dataPathGetter(newItemId);
        const copiedData = get(currentState.data, oldDataPath);
        if (copiedData) {
          set(currentState.data, newDataPath, copiedData);
        }
      }

      return {
        ...currentState,
        tree: newTree,
        data: { ...currentState.data },
      };
    },
    [dataPathGetter, newId]
  );

  /**
   * Moves a node within the tree structure to a new parent at a specified index.
   *
   * @param currentState - The current state of the tree
   * @param action - The action containing the nodeId, parentId, and index for the move operation
   * @returns The new state of the tree with the moved node, or null if the move operation fails
   */
  const move = useCallback((currentState: RateTreeState<TreeItem, TData>, action: MoveAction) => {
    const parent = getParentNodeById(currentState.tree, action.nodeId);
    const newTree = moveNodeInTree<TreeItem>(currentState.tree, action.nodeId, action.parentId, action.index);

    if (isNil(newTree)) {
      return null;
    }

    const expanded = [...without([action.parentId], currentState.expanded), action.parentId].filter((element) =>
      isNotNil(element)
    );
    const isParentEmptyAfterRemove = parent && (getNode(newTree, parent.id)?.children?.length ?? 0) === 0;

    return {
      ...currentState,
      expanded: isParentEmptyAfterRemove ? without([parent.id], expanded) : expanded,
      tree: newTree,
      data: currentState.data,
    };
  }, []);

  const expand = useCallback((currentState: RateTreeState<TreeItem, TData>, action: ExpandAction) => {
    const inextensible = getTreeLeafsIds(currentState.tree);

    if (action.replace) {
      return {
        ...currentState,
        expanded: difference(action.nodeIds, inextensible),
      };
    }

    const expandedIds = action.nodeIds.flatMap((nodeId) => [nodeId, ...collectAncestorIds(currentState.tree, nodeId)]);
    const finalExpanded = [...without(action.nodeIds, currentState.expanded), ...expandedIds].filter((element) =>
      isNotNil(element)
    );

    return {
      ...currentState,
      expanded: difference(finalExpanded, inextensible),
    };
  }, []);

  /**
   * Reducer function that manages the rate tree state with history tracking.
   * Handles various actions like adding, removing, copying, moving nodes, and undo/redo operations.
   */
  const reducer: RateTreeReducer<TreeItem, TData> = (prevState, action) => {
    // Create a function that creates snapshots for history tracking
    const makeSnapshot = snapshot<RateTreeState<TreeItem, TData>>(prevState);
    /**
     * Creates a snapshot if the state has changed, otherwise returns the original state
     */
    const snapshotIfChanged = (newState: RateTreeState<TreeItem, TData> | null) =>
      makeSnapshot(newState ?? prevState.current);

    // Get the current state for convenience
    const currentState = prevState.current;

    switch (action.type) {
      case RateTreeActionTypes.Reset:
        return {
          past: [],
          current: structuredClone(action.data),
          future: [],
        };
      case RateTreeActionTypes.Add:
        return snapshotIfChanged(add(currentState, action));
      case RateTreeActionTypes.Remove:
        return snapshotIfChanged(remove(currentState, action));
      case RateTreeActionTypes.Copy:
        return snapshotIfChanged(copy(currentState, action));
      case RateTreeActionTypes.Move:
        return snapshotIfChanged(move(currentState, action));
      case RateTreeActionTypes.Set:
        return structuredClone(action.state);
      case RateTreeActionTypes.Modify:
        return snapshotIfChanged({
          ...currentState,
          data: action.value,
          changedFields: action.changedFields ?? [],
        });
      case RateTreeActionTypes.Expand:
        return {
          ...prevState,
          current: expand(currentState, action),
        };
      case RateTreeActionTypes.Revert:
        return back(prevState) ?? prevState;
      case RateTreeActionTypes.Forward:
        return forward(prevState) ?? prevState;
      default:
        return prevState;
    }
  };

  const [treeState, dispatch] = useReducer(reducer, structuredClone(initialData));

  options?.onStateChange?.(treeState);

  // reload data if new initial data is provided
  useEffect(() => {
    dispatch({ type: RateTreeActionTypes.Set, state: initialData });
  }, [dispatch, initialData]);

  const { getIndex, getParent, getTreeNode, handleAdd, handleCopy, handleExpand, handleMove, handleRemove } =
    useTreeMethods<TreeItem, TData>(dispatch, treeState);

  const handleSet = useCallback<TM["set"]>((state) => dispatch({ type: RateTreeActionTypes.Set, state }), []);

  const handleSetData = useCallback<RateMethods<TData>["setData"]>(
    (data, changedFields) => {
      dispatch({
        type: RateTreeActionTypes.Modify,
        value: data,
        changedFields: changedFields,
      });
    },
    [dispatch]
  );

  const handleRevert = useCallback<RateBuilderControls["history"]["revert"]>(
    () => dispatch({ type: RateTreeActionTypes.Revert }),
    [dispatch]
  );

  const handleForward = useCallback<RateBuilderControls["history"]["forward"]>(
    () => dispatch({ type: RateTreeActionTypes.Forward }),
    [dispatch]
  );

  return {
    data: treeState.current.data,
    changedFields: treeState.current.changedFields,
    tree: treeState.current.tree,
    expanded: treeState.current.expanded,
    initialData: initialData.current.data,

    get: getTreeNode,
    getParent,
    getIndex,
    add: handleAdd,
    copy: handleCopy,
    remove: handleRemove,
    move: handleMove,
    set: handleSet,

    setData: handleSetData,

    expand: handleExpand,

    history: {
      revert: handleRevert,
      forward: handleForward,
      revertSteps: treeState.past.length,
      forwardSteps: treeState.future.length,
    },
  };
};

const useTreeMethods = <TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues>(
  dispatch: Dispatch<RateTreeActions<TreeItem, TData>>,
  treeState: History<RateTreeState<TreeItem, TData>>
) => {
  type TM = TreeMethods<TreeItem, TData>;

  const getTreeNode = useCallback((nodeId: string) => getNode<TreeItem>(treeState.current.tree, nodeId), [treeState]);

  const getParent = useCallback(
    (nodeId: string) => getParentNodeById<TreeItem>(treeState.current.tree, nodeId),
    [treeState]
  );

  const getIndex = useCallback(
    (nodeId: string) => getIndexOfNodeById<TreeItem>(treeState.current.tree, nodeId),
    [treeState]
  );

  const handleAdd = useCallback<TM["add"]>(
    (parentId, treeItem, data) =>
      dispatch({
        type: RateTreeActionTypes.Add,
        parentId,
        treeItem,
        data,
      }),
    [dispatch]
  );

  const handleCopy = useCallback<TM["copy"]>((nodeId) => dispatch({ type: RateTreeActionTypes.Copy, nodeId }), []);

  const handleRemove = useCallback<TM["remove"]>(
    (nodeId) => dispatch({ type: RateTreeActionTypes.Remove, nodeId }),
    [dispatch]
  );

  const handleMove = useCallback<TM["move"]>(
    (nodeId, parentId, index) =>
      dispatch({
        type: RateTreeActionTypes.Move,
        nodeId,
        parentId,
        index,
      }),
    [dispatch]
  );

  const handleExpand = useCallback<TM["expand"]>(
    (nodeIds, replace) =>
      dispatch({
        type: RateTreeActionTypes.Expand,
        nodeIds,
        replace,
      }),
    [dispatch]
  );

  return {
    getTreeNode,
    getParent,
    getIndex,
    handleAdd,
    handleCopy,
    handleRemove,
    handleMove,
    handleExpand,
  };
};
